import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { FileRejection } from "react-dropzone";

import { FileDropzone } from "@/components";
import { Modal } from "@/components/Modals/Modal";

import { useDeliverScrapeFileMutation } from "@/pages/Private/redux/customEvent/customEvent.api";

import { CustomEvent } from "../schema/customEvent";
import { deliveryErrorKey } from "./scrapeDelivery.errors";

export interface DeliverScrapeFileModalProps {
	customEvent: CustomEvent;
	handleClose: () => void;
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Delivering a scraped file to a source (PREC-3091).
 *
 * One step: the upload attaches the file to the source's list, closes the open request and notifies
 * whoever ordered it. Before this the admin table could only ever attach a *first* file, so a
 * re-scrape had no way of being delivered at all.
 *
 * The dialog says why it will not take a file (PREC-3974). It used to read only the accepted half
 * of the dropzone's answer, so a `.csv` or `.xls` was discarded in silence and left the Deliver
 * button greyed out forever — a dialog that could not be submitted and never explained itself.
 */
export const DeliverScrapeFileModal: FunctionComponent<DeliverScrapeFileModalProps> = ({
	customEvent,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: { name?: string }) =>
			t(`customEvent.rescrape.admin.deliver.${key}`, options),
		[t]
	);

	const [deliverScrapeFile, { isLoading }] = useDeliverScrapeFileMutation();

	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const handleDrop = async (files: File[], rejections: FileRejection[]): Promise<void> => {
		if (files.length) {
			setFile(files[0]);
			setError(null);

			return;
		}

		const rejected = rejections[0];

		if (!rejected) {
			return;
		}

		setFile(null);
		setError(
			rejections.length > 1
				? ts("rejected.tooManyFiles")
				: t("customEvent.rescrape.admin.deliver.rejected.wrongType", {
						name: rejected.file.name,
					})
		);
	};

	const handleDeliver = async () => {
		if (!file) {
			return;
		}

		const form = new FormData();

		form.append("file", file);

		try {
			const delivered = await deliverScrapeFile({ customEventId: customEvent.id, form }).unwrap();

			// PREC-4456: a cancel got there first, so the file became a new delivery. Say so before
			// the dialog goes away, instead of closing as if the request had been closed.
			if (delivered?.cancelledMeanwhileRequestId) {
				setFile(null);
				setNotice(t("customEvent.rescrape.admin.deliveredAfterCancel"));

				return;
			}

			handleClose();
		} catch (err) {
			console.error(err);
			setError(t(`customEvent.rescrape.admin.errors.${deliveryErrorKey(err)}`));
		}
	};

	return (
		<Modal
			disableSave={!file}
			handleClose={handleClose}
			handleSave={handleDeliver}
			isLoading={isLoading}
			isOpened
			size="sm"
			submitButtonText={ts("submit")}
			title={ts("title")}
		>
			<div className="flex flex-col gap-4">
				<div className="text-sm text-text-meta">
					{ts("description", { name: customEvent.name })}
				</div>

				<FileDropzone
					accept={{ [XLSX_MIME]: [".xlsx"] }}
					caption={file ? file.name : ""}
					maxFiles={1}
					onDrop={handleDrop}
				/>

				{error && <div className="text-sm text-status-error">{error}</div>}
				{notice && <div className="text-sm text-text-meta">{notice}</div>}
			</div>
		</Modal>
	);
};
